<script setup lang="ts">
import { scopeWpHtml } from '@/utils/scopeWpHtml';
import { executeWpScripts } from '@/utils/executeWpScripts';

const { promotionsUrlWpApi } = useLinks();

const isLoading = ref<boolean>(false);
const wpPosts = ref<any>([]);

const route = useRoute('promotions-promotion');

const promoSlug = computed(() => {
  return route.params.promotion;
});

const singleItem = computed(() => {
  const filter = wpPosts.value.filter((item: any) => {
    return item.slug === promoSlug.value;
  });

  return filter[0] ?? null;
});

// Remove estilos inline do HTML do WordPress
function sanitizeWpHtml(html: string): string {
  if (!html) return '';

  // Remove todos os atributos style dos elementos
  return html.replace(/\s*style="[^"]*"/gi, '');
}

// Extrai o status do título (mantém texto original com símbolos)
function extractStatus(title: string): string | null {
  if (!title) return null;

  // Remove apenas tags HTML, mantém símbolos como ✦
  const plainText = title.replace(/<[^>]+>/g, '').trim();

  // Procura por INATIVO
  if (plainText.match(/INATIVO/i)) {
    return 'INATIVO';
  }

  // Procura por ENCERRADO
  if (plainText.match(/ENCERRADO/i)) {
    return 'ENCERRADO';
  }

  // Extrai o status que vem após múltiplos espaços e antes de "Atualizado"
  // Padrão: [Título]     [Status]     Atualizado em...
  // Captura texto que vem depois de 2+ espaços e antes de "Atualizado"
  const statusMatch = plainText.match(
    /\s{2,}([✦☆★\s]*(?:B[ÔO]NUS\s+)?ATIVO)\s+(?:Atualizado|$)/i,
  );

  if (statusMatch && statusMatch[1]) {
    return statusMatch[1].trim();
  }

  // Fallback: procura por ATIVO simples se não encontrou com o padrão acima
  if (plainText.match(/\bATIVO\b/i)) {
    return 'ATIVO';
  }

  return null;
}

// Limpa o título removendo informações extras como "ATIVO Atualizado em..."
function sanitizeTitle(title: string): string {
  if (!title) return '';

  // Se o título contém uma tag <h1>, extrai apenas o conteúdo dela
  const h1Match = title.match(/<h1[^>]*>(.*?)<\/h1>/i);

  if (h1Match && h1Match[1]) {
    let cleanTitle = h1Match[1];

    // Remove tags <span> e outros elementos internos, mantendo apenas o texto
    cleanTitle = cleanTitle.replace(/<span[^>]*>.*?<\/span>/gi, '');
    cleanTitle = cleanTitle.replace(/<[^>]+>/g, '');

    // Decodifica entidades HTML (como &#038; que é &)
    cleanTitle = cleanTitle.replace(/&#038;/g, '&');
    cleanTitle = cleanTitle.replace(/&amp;/g, '&');

    // Remove espaços extras
    cleanTitle = cleanTitle.replace(/\s+/g, ' ');

    return cleanTitle.trim();
  }

  // Fallback: se não tem <h1>, usa a lógica anterior
  let cleanTitle = sanitizeWpHtml(title);

  // Remove ATIVO/INATIVO/ENCERRADO e data de atualização
  cleanTitle = cleanTitle.replace(/^\s*(ATIVO|INATIVO|ENCERRADO)\s*/gi, '');
  cleanTitle = cleanTitle.replace(
    /Atualizado em\s+[A-Z]{3}\s+\d{1,2},\s*\d{4},\s*\d{1,2}:\d{2}/gi,
    '',
  );

  return cleanTitle.trim();
}

// Extrai descrição limpa do post (excerpt ou og_description)
function getCleanExcerpt(promo: any): string {
  let text = '';

  // 1. Tenta primeiro o excerpt
  if (promo?.excerpt?.rendered) {
    text = promo.excerpt.rendered;
  }
  // 2. Fallback para og_description do Yoast
  else if (promo?.yoast_head_json?.og_description) {
    return promo.yoast_head_json.og_description;
  }
  // 3. Último fallback: extrai do content.rendered
  else if (promo?.content?.rendered) {
    text = promo.content.rendered;

    // Remove tags <style> e <script> completamente (incluindo conteúdo)
    text = text.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');
    text = text.replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  }
  // 4. Se não tem nada, retorna vazio
  else {
    return '';
  }

  // Remove todas as tags HTML
  text = text.replace(/<[^>]+>/g, '');

  // Decodifica entidades HTML comuns
  text = text.replace(/&nbsp;/g, ' ');
  text = text.replace(/&hellip;/g, '...');
  text = text.replace(/&#\d+;/g, '');
  text = text.replace(/&[a-z]+;/gi, '');

  // Remove espaços extras
  text = text.replace(/\s+/g, ' ').trim();

  // Pega até o primeiro ! ou ?
  const exclamationMatch = text.match(/^[^!?]+[!?]/);

  if (exclamationMatch) {
    return exclamationMatch[0].trim();
  }

  // Último fallback: primeiros 150 caracteres
  return text.substring(0, 150).trim();
}

const wpPostsWithStatus = computed(() =>
  wpPosts.value.map((promo: any) => ({
    ...promo,
    _status: extractStatus(promo?.title?.rendered),
  })),
);

async function getPosts() {
  if (!promotionsUrlWpApi.value) return;

  isLoading.value = true;

  const { ApiService } = useApiService();

  await ApiService.get_external(promotionsUrlWpApi.value)
    .then((res: any) => {
      wpPosts.value = res.data;
      isLoading.value = false;
    })
    .catch((error) => {
      console.error('Erro ao buscar promoções:', error);
      isLoading.value = false;
    });
}

const contentRef = ref<HTMLElement | null>(null);
let cleanupWpScripts: (() => void) | null = null;

watch(
  () => singleItem.value?.content?.rendered,
  async (content) => {
    if (!content) return;
    cleanupWpScripts?.();
    await nextTick();
    if (contentRef.value) {
      cleanupWpScripts = executeWpScripts(contentRef.value);
    }
  },
);

onUnmounted(() => {
  cleanupWpScripts?.();
});

function onBackClick() {
  if (promoSlug.value) {
    navigateTo('/promotions');

    return;
  }

  if (typeof window !== 'undefined' && window.history.length > 1) {
    useRouter().back();

    return;
  }

  navigateTo('/');
}

onMounted(() => {
  getPosts();
});
</script>

<template>
  <div class="promotions-wrapper">
    <section class="promotions-container">
      <header
        class="promotions-header"
        :draggable="false"
      >
        <div class="flex justify-between items-between w-full h-[36px]">
          <div class="flex items-center">
            <button
              aria-label="Voltar"
              class="promotions-back-button"
              @click="onBackClick"
            >
              <NuxtIcon name="arrows/arrow_left" />
            </button>
            <span class="promotions-header-title">
              <h1>
                {{
                  promoSlug
                    ? $t('casino.game.go_back')
                    : $t('sidebar.promotions')
                }}
              </h1>
            </span>
          </div>
        </div>
      </header>

      <section
        v-if="promoSlug"
        class="promotions-view promotions-content-sandbox"
      >
        <div
          v-if="isLoading"
          class="loading"
        >
          <div
            class="load skeleton-loading"
            style="height: 400px"
          ></div>
          <div
            v-for="(n, index) in 30"
            :key="`skeleton-${index}`"
            class="load skeleton-loading"
            style="height: 20px; margin: 15px 0"
          ></div>
        </div>

        <div
          v-if="
            singleItem?.yoast_head_json?.og_image &&
            singleItem?.yoast_head_json?.og_image.length &&
            singleItem?.title?.rendered
          "
          class="thumb wp-thumb"
        >
          <img
            :alt="singleItem?.title?.rendered"
            :src="singleItem?.yoast_head_json?.og_image[0]?.url"
          />
        </div>

        <div
          v-if="singleItem?.title?.rendered"
          class="title wp-title"
        >
          <div v-html="singleItem?.title?.rendered"></div>
        </div>

        <div
          v-if="singleItem?.content?.rendered"
          ref="contentRef"
          class="cactus-promotions content wp-content"
        >
          <div
            class="promotions-content-sandbox"
            v-html="
              scopeWpHtml(
                singleItem.content.rendered,
                '.promotions-content-sandbox',
              )
            "
          ></div>
        </div>

        <div
          v-if="!singleItem && !isLoading"
          class="alert alert-warning"
        >
          <span>{{ $t('no_promotions_found') }}</span>
        </div>
      </section>

      <section
        v-else-if="wpPosts && wpPosts.length"
        class="promotions-section"
      >
        <nuxt-link
          v-for="promo in wpPostsWithStatus"
          :key="promo?.id"
          class="promo"
          :to="`/promotions/${promo.slug}`"
        >
          <div class="promo-content">
            <div
              class="promo-image-wrapper"
              style="border: 1px solid #ff450c"
            >
              <!-- Badge de Status - posicionada no meio do banner -->
              <div
                v-if="promo._status"
                :class="['promo-status-badge', promo._status.toLowerCase()]"
              >
                {{ promo._status }}
              </div>

              <div
                v-if="
                  promo?.yoast_head_json?.og_image &&
                  promo?.yoast_head_json?.og_image.length &&
                  promo?.title?.rendered
                "
                class="promo-image"
              >
                <img
                  :alt="promo?.title?.rendered"
                  :src="promo?.yoast_head_json?.og_image[0]?.url"
                />
              </div>
            </div>

            <div class="promo-info">
              <div class="promo-text">
                <div
                  v-if="promo?.title?.rendered"
                  class="title"
                >
                  <div v-html="sanitizeTitle(promo?.title?.rendered)"></div>
                </div>

                <div class="promo-excerpt">
                  {{ getCleanExcerpt(promo) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Footer unificado para mobile e desktop -->
          <div class="promo-footer">
            <div class="promo-date">
              <NuxtIcon
                class="date-icon"
                name="blog/calendar"
              />
              <div class="date-text">
                <!-- Mobile: formato curto (dd/mm/aaaa) -->
                <span class="md:hidden">
                  {{
                    new Date(promo?.date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                    })
                  }}
                </span>
                <!-- Desktop: formato longo -->
                <span class="hidden md:inline">
                  {{
                    new Date(promo?.date).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: 'long',
                      year: 'numeric',
                    })
                  }}
                </span>
              </div>
            </div>
            <div class="promo-read-more">
              <div class="read-more-text">LER MAIS</div>
              <NuxtIcon
                class="read-more-icon"
                name="mission/button/participar"
              />
            </div>
          </div>
        </nuxt-link>
      </section>
      <section
        v-else-if="isLoading"
        class="promotions-section loading"
      >
        <div
          v-for="(n, index) in 10"
          :key="`skeleton-${index}`"
          class="promo skeleton-loading"
          style="height: 280px"
        ></div>
      </section>
      <section
        v-else
        class="promotions-section"
      >
        <div class="alert alert-warning">
          <span>{{ $t('no_promotions_found') }}</span>
        </div>
      </section>
    </section>
  </div>
</template>

<style scoped lang="scss">
.promotions-view {
  @apply flex justify-between items-stretch flex-wrap gap-[15px] md:gap-[25px] text-left;
  .loading {
    @apply w-full;
    .load {
      @apply flex-1 min-w-[90%] max-w-[100%] rounded overflow-hidden;
    }
  }
  .thumb {
    @apply w-full overflow-hidden relative object-cover object-center bg-bg-secondary flex items-center justify-center rounded;
    img {
      @apply min-w-full min-h-full max-h-full w-auto h-auto object-cover object-center;
    }
  }
  .title {
    @apply text-primary text-base md:text-2xl font-semibold -mt-2;
  }
  .content {
    @apply w-full overflow-hidden;
    img {
      @apply max-w-full;
    }
  }
}

.promotions-section {
  @apply flex justify-start items-stretch flex-wrap gap-[15px] md:gap-[25px];

  .promo {
    @apply p-2.5 md:p-5 rounded-[10px] backdrop-blur-[10px] overflow-hidden cursor-pointer no-underline;
    @apply w-full md:w-[calc(50%-12.5px)];
    background-color: rgb(3, 7, 18);
    outline: 1px solid rgb(38, 38, 38);
    outline-offset: -1px;
    transition: opacity 0.2s;
    display: flex;
    flex-direction: column;

    &:hover {
      @apply opacity-90;
    }

    .promo-content {
      @apply flex flex-col justify-start items-start gap-5;
      flex: 1;

      .promo-image-wrapper {
        @apply w-full rounded-[10px] flex flex-col justify-between items-center;
        overflow: visible;
        position: relative;

        .promo-status-badge {
          @apply px-3 py-1 text-[10px] font-extrabold uppercase rounded-md;
          font-family: 'Plus Jakarta Sans', sans-serif;
          letter-spacing: 0.5px;
          width: fit-content;
          position: absolute;
          top: 0;
          left: 50%;
          transform: translate(-50%, -50%);
          z-index: 10;

          &.ativo {
            background-color: #22c55e;
            color: white;
            box-shadow: 0 0 10px rgba(34, 197, 94, 0.3);
          }

          &.inativo {
            background-color: #6b7280;
            color: white;
            box-shadow: 0 0 10px rgba(107, 114, 128, 0.3);
          }

          &.encerrado {
            background-color: #ef4444;
            color: white;
            box-shadow: 0 0 10px rgba(239, 68, 68, 0.3);
          }
        }

        .promo-image {
          @apply w-full relative overflow-hidden rounded-[10px] bg-black;
          aspect-ratio: 1240 / 541;

          img {
            @apply w-full h-full object-contain object-center rounded-[10px] block;
          }
        }
      }

      .promo-info {
        @apply w-full flex flex-col justify-start items-start gap-0;

        .promo-text {
          @apply w-full px-0 md:px-2.5 pb-0 flex flex-col justify-center items-start gap-2.5;

          .title {
            @apply w-full text-white font-bold;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 24px !important;
            line-height: 1.2 !important;

            :deep(:where(*)) {
              font-size: 24px !important;
              line-height: 1.2 !important;
              font-weight: 700 !important;
              margin: 0 !important;
            }

            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .promo-excerpt {
            @apply w-full text-white/50 font-medium;
            font-family: 'Plus Jakarta Sans', sans-serif;
            font-size: 14px;
            line-height: 1.3;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }
      }
    }

    // Footer unificado para mobile e desktop
    .promo-footer {
      @apply w-full pl-2.5 md:pl-5 pr-2.5 py-1.5 md:py-2.5 bg-white/5 rounded-[10px] flex justify-between items-center mt-5;

      .promo-date {
        @apply px-[5px] md:px-[5px] flex justify-start items-center gap-2;
        width: auto;
        flex: 1;
        margin-top: 0;
        padding: 5px;
        background: transparent;
        border-radius: 0;

        .date-icon {
          @apply text-white/50;
          font-size: 16px;
        }

        .date-text {
          @apply text-white/50 text-xs font-bold leading-4;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }
      }

      .promo-read-more {
        @apply h-8 md:h-10 px-2.5 md:px-5 rounded-md flex justify-center items-center gap-2;
        width: auto;
        margin-top: 0;
        background: radial-gradient(
          ellipse 138% 168.3% at 36.76% -16.67%,
          #ff704a 0%,
          #ff3400 100%
        );
        outline: 0.5px solid rgba(255, 195, 195, 0.3);
        outline-offset: -0.5px;

        .read-more-text {
          @apply text-center text-white text-sm font-extrabold leading-3;
          font-family: 'Plus Jakarta Sans', sans-serif;
        }

        .read-more-icon {
          @apply text-white;
          font-size: 16px;
        }
      }
    }
  }
}

.promotions-wrapper {
  @apply w-full -mt-4 lg:-mt-5;
}

.promotions-container {
  @apply mt-0 md:mt-0 mb-6 md:mb-10;
}

.promotions-header {
  @apply mt-2 mb-2 lg:flex items-center lg:justify-start;
}

.promotions-back-button {
  @apply flex items-center bg-transparent text-texts/60 cursor-pointer;

  svg {
    @apply flex-none;
  }
}

.promotions-header-title {
  @apply inline ml-3 text-[15px] text-texts font-bold select-none;
}
</style>

<style lang="scss">
.cactus-promotions.content {
  @apply text-xs md:text-sm text-texts leading-7;
  a {
    &:hover {
      @apply text-primary underline;
    }
  }

  p {
    @apply leading-7 my-4 text-base;
  }

  img {
    @apply max-w-full h-auto inline-block;
  }

  video {
    @apply max-w-full h-auto inline-block;
  }

  h1 {
    @apply text-[2.5rem];
  }

  h2 {
    @apply text-[2rem];
  }

  div:has(> figure > video) {
    @apply grid grid-cols-[50%,_1fr] grid-rows-[auto];
  }

  figcaption {
    @apply opacity-15;
  }

  ul {
    list-style: inside;

    li {
      @apply my-2;
    }
  }

  ol {
    list-style: decimal inside;

    li {
      @apply my-2;
    }
  }

  blockquote {
    @apply my-4 p-4 bg-bg-secondary rounded border-l-4 border-primary text-texts;
    p {
      @apply m-0;
    }
  }

  .section-title {
    @apply text-primary bg-auth-bg-primary rounded-3xl text-lg font-bold p-2 text-center uppercase mt-12;
  }

  .cta {
    @apply text-bg-primary bg-primary rounded-3xl text-lg font-bold p-4 text-center uppercase mt-12;
  }

  .wp-block-table {
    @apply mb-2.5 mt-1;
    table.has-background {
      border-spacing: 1px;
      border-collapse: separate;
      border: 1px solid white;
      background-color: white !important;

      th {
        @apply text-texts/90 bg-sidebar-bg p-2.5 w-dvw text-center;
      }
      tr td:first-child {
        @apply text-texts/100 bg-sidebar-bg p-2.5;
      }
      tr td:nth-child(2) {
        @apply text-texts/100 bg-sidebar-bg p-2.5;
        width: 80%;
      }
    }
  }
  .wp-block-group-is-layout-flex {
    @apply text-secondary flex flex-col items-center justify-center;
    max-height: 200px !important;
    min-height: 180px !important;
    .has-medium-font-size {
      @apply text-lg m-0 p-0;
    }
  }
  div.cta-container {
    margin-bottom: 10px;
  }
}
.wp-block-social-links {
  @apply flex items-center justify-center flex-nowrap list-none text-lg gap-4 my-4 mx-0 p-0;
  a[href='^http'] {
    @apply text-primary hover:opacity-90 no-underline;
  }
  svg {
    @apply h-[1.25em] w-[1.25em] text-current fill-current;
  }
}
.wp-block-button__link {
  @apply text-bg-primary bg-primary rounded-full shadow-none no-underline py-2 px-5 text-base inline-block text-center break-words cursor-pointer hover:text-bg-primary hover:opacity-80 hover:no-underline;
}
// FIX TO WP
.screen-reader-text {
  @apply overflow-hidden w-[1px] h-[1px];
  clip: rect(1px, 1px, 1px, 1px);
  position: absolute !important;
  word-wrap: normal !important;
}
</style>
